import React, { useCallback, useState } from 'react';
import { RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { operationsApi } from '@/services/api';
import type { OperationStep, OperationsChecklist } from '@/types/operations';
import ChecklistHeader from './ChecklistHeader';
import StepList from './StepList';
import ExemptDialog from './ExemptDialog';
import {
  computeStepCounts,
  getApiErrorMessage,
  getUnresolvedStepKeys,
  isChecklistResolvable,
} from './helpers';
import { Button, Note, confirmColors } from '@/components/ui';
import styles from './OperationsChecklist.module.css';

interface ChecklistViewProps {
  checklist: OperationsChecklist;
  onChange: (next: OperationsChecklist) => void;
}

const ChecklistView: React.FC<ChecklistViewProps> = ({ checklist, onChange }) => {
  const readOnly = checklist.status === 'COMPLETED';

  const [refreshingChecklist, setRefreshingChecklist] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [busyStepId, setBusyStepId] = useState<number | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [exemptTarget, setExemptTarget] = useState<OperationStep | null>(null);

  const applyStepUpdate = useCallback(
    (updated: OperationStep) => {
      const steps = checklist.steps.map((step) => (step.id === updated.id ? updated : step));
      onChange({ ...checklist, steps, step_counts: computeStepCounts(steps) });
    },
    [checklist, onChange],
  );

  const handleRefreshChecklist = useCallback(async () => {
    setRefreshingChecklist(true);
    try {
      const updated = await operationsApi.refreshChecklist(checklist.id);
      onChange(updated);
      toast.success('Checks re-run successfully.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to re-run checks.'));
    } finally {
      setRefreshingChecklist(false);
    }
  }, [checklist.id, onChange]);

  const handleComplete = useCallback(async () => {
    const confirmation = await Swal.fire({
      title: 'Mark checklist complete?',
      text: 'This locks the checklist for the day. You can still view it afterwards.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, complete',
      ...confirmColors('success'),
    });
    if (!confirmation.isConfirmed) return;

    setCompleting(true);
    setCompleteError(null);
    try {
      const updated = await operationsApi.completeChecklist(checklist.id);
      onChange(updated);
      toast.success('Checklist marked complete.');
    } catch (error) {
      const unresolvedKeys = getUnresolvedStepKeys(error);
      if (unresolvedKeys.length > 0) {
        const titleByKey = new Map(checklist.steps.map((step) => [step.step_key, step.title]));
        const titles = unresolvedKeys.map((key) => titleByKey.get(key) ?? key);
        const message = `Resolve these steps first: ${titles.join(', ')}`;
        setCompleteError(message);
        void Swal.fire({ title: 'Unresolved steps', text: message, icon: 'warning' });
      } else {
        const message = getApiErrorMessage(error, 'Failed to complete checklist.');
        setCompleteError(message);
        toast.error(message);
      }
    } finally {
      setCompleting(false);
    }
  }, [checklist.id, checklist.steps, onChange]);

  const handleRefreshStep = useCallback(
    async (step: OperationStep) => {
      setBusyStepId(step.id);
      try {
        const updated = await operationsApi.refreshStep(step.id);
        applyStepUpdate(updated);
        toast.success(`"${step.title}" re-checked.`);
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to re-run check.'));
      } finally {
        setBusyStepId(null);
      }
    },
    [applyStepUpdate],
  );

  const handleConfirmStep = useCallback(
    async (step: OperationStep) => {
      setBusyStepId(step.id);
      try {
        const updated = await operationsApi.confirmStep(step.id);
        applyStepUpdate(updated);
        toast.success(`"${step.title}" confirmed.`);
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to confirm step.'));
      } finally {
        setBusyStepId(null);
      }
    },
    [applyStepUpdate],
  );

  // Rejects on failure so ExemptDialog can surface the `reason` field error inline.
  const handleExemptSubmit = useCallback(
    async (reason: string) => {
      if (!exemptTarget) return;
      const updated = await operationsApi.exemptStep(exemptTarget.id, reason);
      applyStepUpdate(updated);
      toast.success(`"${exemptTarget.title}" exempted.`);
      setExemptTarget(null);
    },
    [applyStepUpdate, exemptTarget],
  );

  const canComplete = isChecklistResolvable(checklist.step_counts);

  return (
    <div>
      <ChecklistHeader
        checklist={checklist}
        actions={
          !readOnly && (
            <div className={styles.toolbar}>
              <Button
                onClick={handleRefreshChecklist}
                disabled={refreshingChecklist}
                icon={<RefreshCw size={15} />}
              >
                {refreshingChecklist ? 'Running…' : 'Re-run checks'}
              </Button>

              <span title={canComplete ? '' : 'Resolve all pending and failed steps first'}>
                <Button
                  variant="success"
                  onClick={handleComplete}
                  disabled={!canComplete || completing}
                  icon={<CheckCircle2 size={15} />}
                >
                  {completing ? 'Completing…' : 'Mark complete'}
                </Button>
              </span>
            </div>
          )
        }
      />

      {completeError && (
        <Note tone="warning" onDismiss={() => setCompleteError(null)}>
          {completeError}
        </Note>
      )}

      <StepList
        steps={checklist.steps}
        readOnly={readOnly}
        busyStepId={busyStepId}
        onRefresh={handleRefreshStep}
        onConfirm={handleConfirmStep}
        onExempt={setExemptTarget}
      />

      <ExemptDialog
        open={Boolean(exemptTarget)}
        step={exemptTarget}
        onSubmit={handleExemptSubmit}
        onClose={() => setExemptTarget(null)}
      />
    </div>
  );
};

export default ChecklistView;
